import OncolensToast from '@/components/OncolensToast';
import { yupResolver } from '@hookform/resolvers/yup';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useGetUser } from '../api/getUser';
import { useUpdateUserOmitFromReporting } from '../api/updateUserOmitFromReporting';
import { UserSettingsDto } from '../types/index';

const userSettingsSchema = yup.object().shape({
  omitFromReporting: yup.boolean(),
});

interface SettingsFormProps {
  userId: string | undefined;
}

function UserSettingsForm({ userId }: SettingsFormProps) {
  const { data: user } = useGetUser(userId);
  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm<UserSettingsDto>({
    resolver: yupResolver(userSettingsSchema),
  });

  const updateUserSettingOmitForReporting = useUpdateUserOmitFromReporting(user?.data?.id);
  const onSubmit = (data: UserSettingsDto) => {
    updateUserSettingOmitForReporting.mutate(data?.omitFromReporting);
  };

  useEffect(() => {
    if (updateUserSettingOmitForReporting.isSuccess) OncolensToast.success('Settings saved');
    if (updateUserSettingOmitForReporting.isError) OncolensToast.error('Error occurred.');
  }, [updateUserSettingOmitForReporting.isError, updateUserSettingOmitForReporting.isSuccess]);

  useEffect(() => {
    setValue('omitFromReporting', user?.data?.omitFromReporting ?? false);
  }, [setValue, user]);

  return (
    <div className="col-span-full">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 divide-y divide-y-blue-gray-200">
        <div className="sm:flex sm:justify-end sm:col-span-6">
          <div className="block pt-2 space-y-2 sm:pt-0 sm:space-y-0 sm:justify-end sm:space-x-4 sm:flex">
            <ButtonWithLoading
              disabled={updateUserSettingOmitForReporting.isLoading}
              loading={updateUserSettingOmitForReporting.isLoading}
              className="w-full sm:w-24"
              buttonColor="blue"
              testSelector="user-settings-save-button"
              type="submit">
              Save
            </ButtonWithLoading>
          </div>
        </div>

        <div className="pt-4">
          <div className="sm:col-span-6">
            <Controller
              name="omitFromReporting"
              control={control}
              render={({ field }) => (
                <div className="col-span-7">
                  <Checkbox
                    data-cy="omit-from-reporting"
                    id="omitFromReporting"
                    htmlName="omitFromReporting"
                    label="Omit from Reporting"
                    value={field.value}
                    onClick={field.onChange}
                  />
                  {errors?.omitFromReporting?.message ? (
                    <p className="mt-2 text-sm text-red-600">
                      {errors?.omitFromReporting?.message}
                    </p>
                  ) : null}
                </div>
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}

export default UserSettingsForm;
